import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';

import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

import { TEAM_ICON_DEFAULT } from '../../../common/staticData';
import TeamEditDialog from './teamEditDialog';
import gameService from '../../../services/game.service';
import {
    setTeamsList,
} from '../../../helpers/coachSlice';

const TeamListItem = ({ team, isHover, t }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);

    // Calculate points (3 for win, 1 for draw)
    const points = (team.won || 0) * 3 + (team.draw || 0);

    // Calculate goal difference
    const goalDiff = (team.goals || 0) - (team.opponent_goals || 0);
    const goalDiffColor = goalDiff > 0 ? '#2e7d32' : goalDiff < 0 ? '#d32f2f' : '#757575';
    const goalDiffText = goalDiff > 0 ? `+${goalDiff}` : goalDiff.toString();

    // Calculate win rate
    const totalGames = team.total_game_played || 0;
    const winRate = totalGames > 0 ? Math.round(((team.won || 0) / totalGames) * 100) : 0;

    // Calculate average goals per game
    const avgGoals = totalGames > 0 ? ((team.goals || 0) / totalGames).toFixed(1) : '0.0';
    const avgConceded = totalGames > 0 ? ((team.opponent_goals || 0) / totalGames).toFixed(1) : '0.0';

    const handleChangePath = (teamId, seasonId, leagueId, imageUrl) => () => {
        const encodedImageUrl = encodeURIComponent(imageUrl);
        navigate(`/new_coach/teams/${btoa(`${teamId}|${seasonId}|${leagueId}|${encodedImageUrl}`)}`);
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    const updateTeam = () => {
        gameService.getAllMyCoachTeam().then((res) => {
            dispatch(setTeamsList(res));
        })
    };

    // Stat box component for consistent styling
    const StatBox = ({ label, value, color, isLarge = false }) => (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: isLarge ? 70 : 50
        }}>
            <Typography sx={{
                fontSize: '10px',
                color: '#888',
                textTransform: 'uppercase',
                fontWeight: 500,
                letterSpacing: '0.5px'
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontSize: isLarge ? '18px' : '14px',
                fontWeight: 600,
                color: color || '#333'
            }}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                marginBottom: '12px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                boxShadow: isHover ? '0px 4px 20px rgba(0, 0, 0, 0.12)' : '0px 1px 4px rgba(0, 0, 0, 0.05)',
                transform: isHover ? 'translateY(-2px)' : 'none',
                border: '1px solid #e8e8e8'
            }}
        >
            {/* Team Info Section */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 20px',
                    gap: '16px',
                    borderRight: '1px solid #f0f0f0',
                    width: '25%',
                    minWidth: 200
                }}
                onClick={handleChangePath(team.team_id, team.season_id, team.league_id, team.team_image)}
            >
                <Box sx={{
                    width: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    flexShrink: 0
                }}>
                    <img
                        style={{ maxHeight: '48px', maxWidth: '48px', objectFit: 'contain' }}
                        src={team.team_image
                            ? (team.team_image.includes(process.env.REACT_APP_S3_URI)
                                ? team.team_image
                                : process.env.REACT_APP_S3_URI + team.team_image)
                            : TEAM_ICON_DEFAULT}
                        alt="Team Logo"
                    />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#1a1a1a',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {team.team_name}
                    </Typography>
                    <Typography sx={{
                        fontSize: '12px',
                        color: '#666',
                        mt: 0.5
                    }}>
                        {team.league_name} | {team.season_name}
                    </Typography>
                </Box>
            </Box>

            {/* Performance Section - Win Rate & Avg Goals */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 20px',
                    gap: '28px',
                    borderRight: '1px solid #f0f0f0',
                    width: '22%',
                    minWidth: 220
                }}
                onClick={handleChangePath(team.team_id, team.season_id, team.league_id, team.team_image)}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 500 }}>
                        {t('Win Rate')}
                    </Typography>
                    <Typography sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: winRate >= 50 ? '#2e7d32' : winRate >= 30 ? '#ed6c02' : '#d32f2f'
                    }}>
                        {winRate}%
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 500 }}>
                        {t('Avg Scored')}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2e7d32' }}>
                        {avgGoals}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 500 }}>
                        {t('Avg Conceded')}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#d32f2f' }}>
                        {avgConceded}
                    </Typography>
                </Box>
            </Box>

            {/* Results Section - W/D/L */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 20px',
                    gap: '12px',
                    backgroundColor: '#fafafa',
                    borderRight: '1px solid #f0f0f0',
                    width: '15%',
                    minWidth: 150
                }}
                onClick={handleChangePath(team.team_id, team.season_id, team.league_id, team.team_image)}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#e8f5e9',
                    padding: '6px 10px',
                    borderRadius: '6px'
                }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#2e7d32' }}>W</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2e7d32' }}>{team.won || 0}</Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#f5f5f5',
                    padding: '6px 10px',
                    borderRadius: '6px'
                }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#757575' }}>D</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#757575' }}>{team.draw || 0}</Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#ffebee',
                    padding: '6px 10px',
                    borderRadius: '6px'
                }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#d32f2f' }}>L</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#d32f2f' }}>{team.lost || 0}</Typography>
                </Box>
            </Box>

            {/* Stats Section */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    gap: '20px',
                    borderRight: '1px solid #f0f0f0',
                    width: '18%',
                    minWidth: 160
                }}
                onClick={handleChangePath(team.team_id, team.season_id, team.league_id, team.team_image)}
            >
                <StatBox label={t('Games')} value={team.total_game_played || 0} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 500 }}>
                        {t('Goals')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2e7d32' }}>
                            {team.goals || 0}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#999' }}>:</Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#d32f2f' }}>
                            {team.opponent_goals || 0}
                        </Typography>
                    </Box>
                </Box>
                <StatBox label={t('GD')} value={goalDiffText} color={goalDiffColor} />
            </Box>

            {/* Players Section */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    gap: '10px',
                    borderRight: '1px solid #f0f0f0',
                    width: '12%',
                    minWidth: 100
                }}
                onClick={handleChangePath(team.team_id, team.season_id, team.league_id, team.team_image)}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#e3f2fd'
                }}>
                    <GroupIcon sx={{ color: '#1976d2', fontSize: 22 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 500 }}>
                        {t('Players')}
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1976d2' }}>
                        {team.player_count || 0}
                    </Typography>
                </Box>
            </Box>

            {/* Settings Button */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '8%',
                    minWidth: 50,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        backgroundColor: '#f5f5f5'
                    }
                }}
                onClick={() => setOpen(true)}
            >
                <SettingsIcon sx={{ color: '#999', fontSize: 22, '&:hover': { color: '#1976d2' } }} />
            </Box>
            <TeamEditDialog open={open} t={t} onClose={handleCloseDialog} team={team} onUpdateTeam={updateTeam} />
        </Box>
    );
};

export default TeamListItem;
